import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import acApi from 'axios/ac/acApi';
import { Service } from 'types/rest/assetCatalogTypes';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';

/** Supported auth modes matching the backend {@code AuthType} enum. */
type AuthType = 'NONE' | 'BASIC' | 'API_TOKEN';

interface ImportFromUrlModalProps {
  /** Whether the modal is visible. */
  show: boolean;
  /** Pre-selected service ID, if any. */
  preselectedServiceId?: number;
  /** Called when the modal should close (cancel or success). */
  onHide: () => void;
  /** Called after a successful import, with the number of transactions created. */
  onSuccess: (count: number) => void;
}

/**
 * Modal that lets users import transactions from a URL.
 *
 * Supports WireMock admin endpoints and publicly-hosted OpenAPI 3.0 specs.
 * Auth modes: NONE, BASIC (username/password), and API_TOKEN (appended as a
 * query parameter by the server — relevant for WireMock endpoints).
 */
const ImportFromUrlModal: React.FC<ImportFromUrlModalProps> = ({ show, preselectedServiceId, onHide, onSuccess }) => {
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState<AuthType>('NONE');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [serviceId, setServiceId] = useState<number | null>(preselectedServiceId ?? null);
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync serviceId whenever the modal is opened or preselectedServiceId changes so that
  // a stale selection from a previous open/close cycle is never shown.
  useEffect(() => {
    if (show) {
      setServiceId(preselectedServiceId ?? null);
    }
  }, [show, preselectedServiceId]);

  /** Simple client-side URL validation — syntactic check only. */
  const isUrlValid = (): boolean => {
    try {
      const parsed = new URL(url.trim());
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isFormValid = (): boolean => {
    if (!isUrlValid() || serviceId === null) {
      return false;
    }
    if (authType === 'BASIC' && (!username.trim() || !password.trim())) {
      return false;
    }
    if (authType === 'API_TOKEN' && !apiToken.trim()) {
      return false;
    }
    return true;
  };

  const handleServiceSelect = (selected: Service | null) => {
    setServiceId(selected ? selected.id : null);
  };

  const buildRequestBody = () => {
    const tagList = tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    return {
      url: url.trim(),
      authType,
      username: authType === 'BASIC' ? username : undefined,
      password: authType === 'BASIC' ? password : undefined,
      apiToken: authType === 'API_TOKEN' ? apiToken : undefined,
      tags: tagList.length > 0 ? tagList : undefined,
    };
  };

  const handleImport = async () => {
    if (!isFormValid() || serviceId === null) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await acApi.transactions.importFromUrl(serviceId, buildRequestBody());
      const result = response.data?.result ?? [];
      onSuccess(result.length);
      handleClose();
    } catch (err) {
      const message = err?.response?.data?.error ?? 'Import failed. Please check the URL and credentials.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setAuthType('NONE');
    setUsername('');
    setPassword('');
    setApiToken('');
    setTags('');
    setServiceId(preselectedServiceId ?? null);
    setError(null);
    setLoading(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Import from URL</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* URL */}
        <Form.Group className="mb-3" controlId="importUrl">
          <Form.Label>URL</Form.Label>
          <Form.Control
            type="url"
            placeholder="https://example.com/__admin/mappings or https://example.com/openapi.json"
            value={url}
            onChange={e => setUrl(e.target.value)}
            isInvalid={url.length > 0 && !isUrlValid()}
          />
          <Form.Control.Feedback type="invalid">URL must start with http:// or https://</Form.Control.Feedback>
        </Form.Group>

        {/* Auth type */}
        <Form.Group className="mb-3" controlId="authType">
          <Form.Label>Authentication</Form.Label>
          <Form.Control as="select" value={authType} onChange={e => setAuthType(e.target.value as AuthType)}>
            <option value="NONE">None</option>
            <option value="BASIC">Basic (username / password)</option>
            <option value="API_TOKEN">API Token</option>
          </Form.Control>
        </Form.Group>

        {/* BASIC credentials */}
        {authType === 'BASIC' && (
          <>
            <Form.Group className="mb-3" controlId="importUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="importPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </Form.Group>
          </>
        )}

        {/* API token */}
        {authType === 'API_TOKEN' && (
          <Form.Group className="mb-3" controlId="importApiToken">
            <Form.Label>API Token</Form.Label>
            <Form.Control
              type="password"
              value={apiToken}
              onChange={e => setApiToken(e.target.value)}
              autoComplete="off"
            />
          </Form.Group>
        )}

        {/* Service selector */}
        <Form.Group className="mb-3" controlId="importService">
          <Form.Label>Target Service</Form.Label>
          <ServicePicker
            selectHandler={handleServiceSelect}
            isSelectAll={false}
            isCreateEnabled={true}
            manualPreLoad={true}
            preSelected={serviceId ?? 0}
            maxHeight="230px"
            id="importFromUrlServicePicker"
            focus={false}
          />
        </Form.Group>

        {/* Tags */}
        <Form.Group className="mb-3" controlId="importTags">
          <Form.Label>Tags (comma-separated, optional)</Form.Label>
          <Form.Control type="text" placeholder="v1, prod" value={tags} onChange={e => setTags(e.target.value)} />
        </Form.Group>

        {/* Inline error */}
        {error && (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={!isFormValid() || loading}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-1" />
              Importing…
            </>
          ) : (
            'Import'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportFromUrlModal;
